import dayjs from 'dayjs';
import { buildParams } from '../../helpers/path';
import { PaginationType } from '../../types/pagination';
import { CustomCacheRequest, InfiniteScrollRequest, SortRequest } from '../../types/props';
import { getLanguageLabel } from '../../helpers/language-labels';
import { managerApiUrl } from './manager-api';
import {
  fetchAggregationAvailableResources,
  fetchExternalResourceLanguages,
  unwrapManagerData,
} from './monitoring-api';
import { baseFetch } from './utils.ts/base-fetch';
import { ExternalReviewSourceType } from './external-reviews-api';

/** Значение query `review_language` для строк без language_id */
export const EXTERNAL_REVIEW_LANGUAGE_PENDING = 'pending';

export type ExternalReviewItemSentimentType = 'positive' | 'negative' | 'mixed';

export type ExternalReviewItemSentimentPercentages = {
  positive: number;
  negative: number;
  neutral: number;
};

export type ExternalReviewItemType = {
  id: number;
  /** Уникальный ключ строки в UI (uuid с бэка или составной fallback при дублях id) */
  rowKey: string;
  source: ExternalReviewSourceType | null;
  comment: string;
  /** Код языка из GET languages (`name`), нижний регистр; пусто если language_id null */
  language_code: string;
  /** Подпись для колонки «Язык» (расшифровка кода); пусто если «в процессе» — текст в UI */
  language_label: string;
  /** @deprecated используйте language_label; оставлено для сортировки по колонке */
  language: string;
  language_id: number | null;
  /** positive/neutral/negative с бэка все null — тональность ещё не посчитана */
  sentiment_in_progress: boolean;
  sentiment: ExternalReviewItemSentimentType;
  sentiment_percentages: ExternalReviewItemSentimentPercentages;
  created_at: string;
  profile_url?: string;
  author?: string;
  rating?: number;
};

/** Ответ GET .../stored-comments */
export type StoredCommentApiRow = {
  id?: number;
  uuid?: string;
  author?: string;
  language_id?: number | null;
  /** 1 — негатив, 2 — нейтраль/смешанный, 3 — позитив */
  sentiment?: number;
  resourceId?: number;
  resource_id?: number;
  text?: string;
  positive?: number | null;
  neutral?: number | null;
  negative?: number | null;
  /** Плоская строка даты с бэка, напр. `2026-03-15 14:58:02` */
  created_at?: string;
  createdAt?: {
    date?: string;
    timezone_type?: number;
    timezone?: string;
  };
};

export type FetchExternalReviewItemsRequest = {
  source?: string;
  comment?: string;
  review_language?: string;
  sentiment?: ExternalReviewItemSentimentType;
  offset?: number;
  /** Unix sec; если заданы вместе с from/to, приоритет у from/to */
  begin?: number;
  end?: number;
  /** Локальные ISO-строки из фильтра даты колонки; перекрывают begin/end */
  from?: string;
  to?: string;
} & SortRequest &
  CustomCacheRequest &
  InfiniteScrollRequest;

function parseCreatedAtString(createdAt?: StoredCommentApiRow['createdAt']): string {
  const d = createdAt?.date;
  if (!d || typeof d !== 'string') return '';
  return d.trim();
}

function resolveCommentCreatedAt(row: StoredCommentApiRow): string {
  const flat = typeof row.created_at === 'string' ? row.created_at.trim() : '';
  if (flat) return flat;
  return parseCreatedAtString(row.createdAt);
}

/** Все три счётчика отсутствуют (null/undefined) — как в ответе до расчёта тональности */
export function isStoredCommentSentimentPending(row: StoredCommentApiRow): boolean {
  const missing = (v: unknown) => v === null || v === undefined;
  return missing(row.positive) && missing(row.neutral) && missing(row.negative);
}

export function shouldIncludeStoredComment(row: StoredCommentApiRow): boolean {
  const t = (row.text ?? '').trim();
  if (!t) return false;
  if (t.toUpperCase() === 'NO_COMMENTS') return false;
  return true;
}

/** Имя источника из GET aggregation/available → ключ иконки в `companySocialIcons` */
export function aggregationDisplayNameToIconKey(displayName: string): string {
  const n = displayName.trim().toLowerCase().replace(/\s+/g, '');
  const map: Record<string, string> = {
    '2gis': '2gis',
    instagram: 'instagram',
    vk: 'vk',
    facebook: 'facebook',
    tiktok: 'tiktok',
    youtube: 'youtube',
    googlemaps: 'googlemaps',
    yandexmaps: 'yandexmaps',
    otzovik: 'otzovik',
    yandexmarket: 'yandexmarket',
    kaspimarket: 'kaspi',
    wildberries: 'wildberries',
    ozon: 'ozon',
    linkedin: 'linkedin',
  };
  return map[n] ?? 'telegram';
}

export type AggregationSourceMeta = { name: string; icon: string };

/** Человекочитаемая подпись по коду из GET external-resource/languages */
export function displayLabelForStoredCommentLanguageCode(code: string): string {
  return getLanguageLabel(code);
}

function mapStoredCommentSentiment(
  apiSentiment: number | undefined,
  p: number,
  neu: number,
  neg: number,
): ExternalReviewItemSentimentType {
  if (apiSentiment === 1) return 'negative';
  if (apiSentiment === 2) return 'mixed';
  if (apiSentiment === 3) return 'positive';
  const sum = p + neu + neg;
  if (sum <= 0) return 'mixed';
  if (p > neu && p > neg) return 'positive';
  if (neg > p && neg > neu) return 'negative';
  return 'mixed';
}

/** Ключ для таблицы / разворота комментария: uuid или составной ключ */
export function buildStoredCommentRowKey(row: StoredCommentApiRow): string {
  const u = row.uuid?.trim();
  if (u) return u;
  const created = resolveCommentCreatedAt(row);
  const text = row.text ?? '';
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  }
  const idPart =
    row.id != null && Number.isFinite(Number(row.id)) ? String(row.id) : 'noid';
  const authorPart = (row.author ?? '').slice(0, 48);
  return `${idPart}__${authorPart}__${created}__${text.length}_${h}`;
}

export function buildAggregationSourcesById(
  list: { id: number; name: string }[],
): Map<number, AggregationSourceMeta> {
  const m = new Map<number, AggregationSourceMeta>();
  for (const r of list) {
    m.set(r.id, {
      name: r.name,
      icon: aggregationDisplayNameToIconKey(r.name),
    });
  }
  return m;
}

export function mapStoredCommentToItem(
  row: StoredCommentApiRow,
  sourcesById?: Map<number, AggregationSourceMeta>,
  languagesById?: Map<number, string>,
): ExternalReviewItemType {
  const sentimentPending = isStoredCommentSentimentPending(row);
  const p = sentimentPending ? 0 : Number(row.positive ?? 0);
  const neu = sentimentPending ? 0 : Number(row.neutral ?? 0);
  const neg = sentimentPending ? 0 : Number(row.negative ?? 0);
  const sum = p + neu + neg;

  let posPct = 0;
  let neuPct = 0;
  let negPct = 0;
  if (sum > 0) {
    posPct = (p / sum) * 100;
    neuPct = (neu / sum) * 100;
    negPct = (neg / sum) * 100;
  }

  const sentiment = sentimentPending
    ? 'mixed'
    : mapStoredCommentSentiment(row.sentiment, p, neu, neg);

  const lid = row.language_id;
  const hasLanguageId = lid != null && Number.isFinite(Number(lid));
  const rawCode = hasLanguageId ? (languagesById?.get(Number(lid)) ?? '').trim() : '';
  const language_code = rawCode.toLowerCase();
  const language_label =
    hasLanguageId && language_code
      ? displayLabelForStoredCommentLanguageCode(language_code)
      : hasLanguageId
        ? `ID ${lid}`
        : '';

  const resourceId = Number(row.resourceId ?? row.resource_id);
  const fromCatalog =
    Number.isFinite(resourceId) && sourcesById?.has(resourceId)
      ? sourcesById.get(resourceId)!
      : null;

  const source: ExternalReviewSourceType | null = Number.isFinite(resourceId)
    ? fromCatalog ?? {
        name: `Источник #${resourceId}`,
        icon: 'telegram',
      }
    : null;

  const numericId =
    row.id != null && Number.isFinite(Number(row.id)) ? Number(row.id) : 0;

  return {
    id: numericId,
    rowKey: buildStoredCommentRowKey(row),
    source,
    comment: row.text ?? '',
    language_id: hasLanguageId ? Number(lid) : null,
    language_code,
    language_label,
    language: language_label,
    author: row.author,
    sentiment_in_progress: sentimentPending,
    sentiment,
    sentiment_percentages: {
      positive: Math.round(posPct * 10) / 10,
      neutral: Math.round(neuPct * 10) / 10,
      negative: Math.round(negPct * 10) / 10,
    },
    created_at: resolveCommentCreatedAt(row),
  };
}

function extractRawRows(unwrapped: unknown): StoredCommentApiRow[] {
  if (Array.isArray(unwrapped)) {
    return unwrapped as StoredCommentApiRow[];
  }
  if (unwrapped && typeof unwrapped === 'object') {
    const o = unwrapped as Record<string, unknown>;
    const list = o.external_review_items ?? o.items ?? o.data;
    if (Array.isArray(list)) return list as StoredCommentApiRow[];
  }
  return [];
}

/**
 * Одна загрузка полного списка; пагинация на клиенте (Table `frontPagination`).
 * Параметры `page` / `page_size` / `offset` в запрос не передаются.
 */
export const fetchExternalReviewItems = async (
  params?: FetchExternalReviewItemsRequest,
): Promise<PaginationType & { external_review_items: ExternalReviewItemType[] }> => {
  let url = `${managerApiUrl}/external-resource/stored-comments`;

  const { customCache, ...rest } = params || {};
  const queryParams: Record<string, unknown> = { ...rest };
  delete queryParams.page;
  delete queryParams.page_size;
  delete queryParams.offset;
  delete queryParams.infinite;
  delete queryParams.field;
  delete queryParams.dir;
  delete queryParams.language;

  const fromStr = typeof queryParams.from === 'string' ? queryParams.from : undefined;
  const toStr = typeof queryParams.to === 'string' ? queryParams.to : undefined;
  const presetBegin = typeof queryParams.begin === 'number' ? queryParams.begin : undefined;
  const presetEnd = typeof queryParams.end === 'number' ? queryParams.end : undefined;
  delete queryParams.from;
  delete queryParams.to;
  delete queryParams.begin;
  delete queryParams.end;

  let begin: number | undefined;
  let end: number | undefined;
  if (fromStr && toStr) {
    const b = dayjs(fromStr).unix();
    const e = dayjs(toStr).unix();
    if (Number.isFinite(b)) begin = b;
    if (Number.isFinite(e)) end = e;
  } else {
    begin = presetBegin;
    end = presetEnd;
  }
  if (begin !== undefined && Number.isFinite(begin)) queryParams.begin = begin;
  if (end !== undefined && Number.isFinite(end)) queryParams.end = end;

  const qs = buildParams(queryParams as Record<string, string | number | undefined>);
  if (qs) url += qs;

  const [commentsRaw, availableList, languagesList] = await Promise.all([
    baseFetch(url, {
      customCache,
    }),
    fetchAggregationAvailableResources().catch(() => [] as { id: number; name: string }[]),
    fetchExternalResourceLanguages().catch(() => [] as { id: number; name: string }[]),
  ]);

  const sourcesById = buildAggregationSourcesById(availableList);
  const languagesById = new Map(languagesList.map((l) => [l.id, l.name]));

  const unwrapped = unwrapManagerData<unknown>(commentsRaw);
  const rawRows = extractRawRows(unwrapped ?? commentsRaw).filter(shouldIncludeStoredComment);
  const keyCounters = new Map<string, number>();
  const external_review_items = rawRows.map((r, index) => {
    const item = mapStoredCommentToItem(r, sourcesById, languagesById);
    const prev = keyCounters.get(item.rowKey) ?? 0;
    keyCounters.set(item.rowKey, prev + 1);

    if (prev === 0) return item;

    // Защита от дублей одинаковых записей в одном ответе API.
    return {
      ...item,
      rowKey: `${item.rowKey}__dup${prev}__${index}`,
    };
  });

  const n = external_review_items.length;
  return {
    external_review_items,
    count: n,
    page: 1,
    page_size: Math.max(1, n || 1),
    total_pages: 1,
    is_last: true,
    last: 1,
  };
};
